import type React from 'react';

import { Box, Divider, FieldSet, FormField, Input, Label, Legend } from '@zillow/constellation-10';

import type { Section } from '../../../../models/index';
import { HEATING_OIL, UTILITIES_INFORMATION } from '../../../../constants';
import HeaderToggle from '../Utilities/HeaderToggle';
import SwitchWithTitle from '../../../Widgets/SwitchWithTitle';
import { UTILITIES_HEADING_AND_CONTENT } from '../../../../constants/stringConstants';
import RadioListBox from '../../../Widgets/RadioListBox';
import CurrencyInput from '../../../Widgets/CurrencyInput';
import PhoneInput from '../../../Widgets/PhoneInput';

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
  additionalFields?: object;
};

const INITIAL_STATE = {
  [HEATING_OIL.FIELD_NAME.IS_ENABLED]: true,
  [HEATING_OIL.FIELD_NAME.TENANT_PAYS_IN_ADDITION_TO_RENT]: false,
  [HEATING_OIL.FIELD_NAME.SERVICE_PROVIDER_TENANT_RESPONSIBILITY]: null,
  [UTILITIES_INFORMATION.FIELD_NAME.HEATING_OIL_PHONE_NUMBER]: null,
  [UTILITIES_INFORMATION.FIELD_NAME.HEATING_OIL_SERVICE_PROVIDER]: null,
  [HEATING_OIL.FIELD_NAME.FIXED_AMOUNT_VALUE]: null,
  [HEATING_OIL.FIELD_NAME.LANDLORD_RESPONSIBILITY]: false,
  [HEATING_OIL.FIELD_NAME.TENANT_RESPONSIBILITY]: false,
};

function HeatingOil_v4({ additionalFields, section, setSectionValues }: Props) {
  const heatingOil_isEnabled = section.getFieldValue(HEATING_OIL.FIELD_NAME.IS_ENABLED);
  const heatingOil_paysInAdditionIsToggled = section.getFieldValue(
    HEATING_OIL.FIELD_NAME.TENANT_PAYS_IN_ADDITION_TO_RENT,
  );
  const landlordSelected = section.getFieldValue(HEATING_OIL.FIELD_NAME.LANDLORD_RESPONSIBILITY);
  const fixedAmountValue = section.getFieldValue(HEATING_OIL.FIELD_NAME.FIXED_AMOUNT_VALUE);

  function toggleUtility(): void {
    setSectionValues({
      ...INITIAL_STATE,
      [HEATING_OIL.FIELD_NAME.IS_ENABLED]: !section.getFieldValue(HEATING_OIL.FIELD_NAME.IS_ENABLED),
      ...additionalFields,
    });
  }

  function togglePaysInAddition(): void {
    setSectionValues({
      ...INITIAL_STATE,
      [HEATING_OIL.FIELD_NAME.TENANT_PAYS_IN_ADDITION_TO_RENT]: !section.getFieldValue(
        HEATING_OIL.FIELD_NAME.TENANT_PAYS_IN_ADDITION_TO_RENT,
      ),
      ...additionalFields,
    });
  }

  function setServiceProvider(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
    setSectionValues({
      [UTILITIES_INFORMATION.FIELD_NAME.HEATING_OIL_SERVICE_PROVIDER]: event.currentTarget.value,
    });
  }

  function setFixedAmountValue(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
    setSectionValues({
      [HEATING_OIL.FIELD_NAME.FIXED_AMOUNT_VALUE]: event.currentTarget.value,
    });
  }

  function onServiceProviderPhoneNumberChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    const { value } = event.currentTarget;

    setSectionValues({
      [UTILITIES_INFORMATION.FIELD_NAME.HEATING_OIL_PHONE_NUMBER]: value,
    });
  }

  return (
    <>
      <HeaderToggle
        header="Heating oil"
        subheader="Applies to this property"
        switchOn={heatingOil_isEnabled}
        onSwitchToggle={toggleUtility}
      />
      {heatingOil_isEnabled && (
        <>
          <Divider css={{ marginTop: 'layout.tighter', marginBottom: 'layout.tighter' }} />
          <FormField
            required
            css={{ marginBottom: 'layout.tight' }}
            label={<Label>What service provider?</Label>}
            control={
              <Input
                value={section.getFieldValue(UTILITIES_INFORMATION.FIELD_NAME.HEATING_OIL_SERVICE_PROVIDER)}
                onChange={setServiceProvider}
              />
            }
          />
          <FormField
            required
            css={{ marginBottom: 'layout.tight' }}
            label={<Label>What is the service provider’s number?</Label>}
            control={
              <PhoneInput
                value={section.getFieldValue(UTILITIES_INFORMATION.FIELD_NAME.HEATING_OIL_PHONE_NUMBER)}
                onChange={onServiceProviderPhoneNumberChange}
              />
            }
          />
          <SwitchWithTitle
            text="Tenant pays in addition to base rent"
            onClick={togglePaysInAddition}
            toggled={heatingOil_paysInAdditionIsToggled}
          />
          {heatingOil_paysInAdditionIsToggled && (
            <Box css={{ marginTop: 'layout.tight' }}>
              <FieldSet required legend={<Legend>Who will be responsible for heating oil set up?</Legend>}>
                <RadioListBox
                  section={section}
                  setSectionValues={setSectionValues}
                  option1FieldName={HEATING_OIL.FIELD_NAME.TENANT_RESPONSIBILITY}
                  option2FieldName={HEATING_OIL.FIELD_NAME.LANDLORD_RESPONSIBILITY}
                  option1Header={UTILITIES_HEADING_AND_CONTENT.option1Heading}
                  option1Content="Responsible for account set up and direct payments to previously specified service provider"
                  option2Header={UTILITIES_HEADING_AND_CONTENT.option2Heading}
                  option2Content={UTILITIES_HEADING_AND_CONTENT.option2Content}
                />
              </FieldSet>
              {landlordSelected && (
                <FormField
                  required
                  css={{ marginTop: 'layout.tight' }}
                  label={<Label>What is the additional fixed fee for heating oil?</Label>}
                  control={
                    <CurrencyInput
                      aria-label="fixed amount"
                      data-test="fixedAmountValue"
                      value={fixedAmountValue || ''}
                      onBlur={setFixedAmountValue}
                      onChange={setFixedAmountValue}
                      endAdornment="per month"
                    />
                  }
                />
              )}
            </Box>
          )}
        </>
      )}
    </>
  );
}

HeatingOil_v4.description = 'Move service provider to above.';

export default HeatingOil_v4;
